/**
 * Native port of the v1 `endpointUrl` factory (account-v1/src/core/api/
 * touchpoint.js:33-126) — the touchpoint data/styling/integration URL builder.
 * WS5 #10 Stage A inc 2w: replaces the `$injector.get('endpointUrl')` grabs
 * (ft-touchpoint-messaging now; ft-touchpoint-integration next). Pure functions,
 * ported VERBATIM: the same emailTouchpointsUriMap / touchpointUrlParts /
 * layerUrlParts tables, the same dead `layerUrlName == 'photocontest'` ternary
 * (getLayerUrlName never returns 'photocontest' — kept as v1 wrote it), lodash
 * `_.find(arr, {key})` → Array.find, `angular.isDefined(version)` →
 * `version !== undefined`. Exported as an `endpointUrl` object so call sites
 * keep the v1 shape (`endpointUrl.getDataUrl(...)`).
 */

const emailTouchpointsUriMap: Record<string, string> = {
  finalistsEmail: 'emails/finalist',
  reminderEmail: 'emails/finalistreminder',
  activityEmail: 'emails/finalistactivity',
  winnerEmail: 'emails/winnernotification',
  'invitation-email': 'emails/invitation-email',
  'reminder-emails': 'emails/reminder-emails',
  'reengagement-emails': 'emails/reengagement-emails',
  homepageEmail: 'emails/homepage',
  followup: 'emails/followup',
};

const touchpointUrlParts = [
  //{ type: 'poststay', postfix: 'email' },
  { type: 'quote', postfix: 'data' },
  { type: 'predefinedQuote', postfix: 'quote' },
  { type: 'homepageEmail', postfix: 'email' },
  { type: 'homepage', postfix: 'data' },
  { type: 'contestPage', postfix: 'data' },
];

const layerUrlParts = [
  { name: 'pre-stay', urlName: 'prestay' },
  { name: 'post-stay', urlName: 'poststay' },
  { name: 'passkey-events', urlName: 'passkeyevents' },
  { name: 'photo-advocacy', urlName: 'photo-advocacy' },
  { name: 'group-advocacy', urlName: 'groupadvocacy' },
  { name: 'quote-list', urlName: 'quotelist' },
];

function getLayerUrlName(layerName: string): string {
  const urlParts = layerUrlParts.find((p) => p.name === layerName);
  return urlParts ? urlParts.urlName : layerName;
}

function getDataUrl(layerName: string, touchpointType: string, groupId: string, propertyUuid: string, version?: any): string {
  let layerUrlName = getLayerUrlName(layerName);
  const emailPostfix = emailTouchpointsUriMap[touchpointType];
  layerUrlName = layerUrlName == 'photocontest' && emailPostfix ? layerName : layerUrlName;
  let basePart = '/api/' + propertyUuid + '/properties/layers/' + layerUrlName + '/touchpoints/' + groupId + '/';
  if (version !== undefined) {
    basePart += 'versions/' + version + '/';
  }
  const defaultEndOfUrl = touchpointType + '/data';
  const parts = touchpointUrlParts.find((p) => p.type === touchpointType);
  if (emailPostfix) {
    return basePart + emailPostfix + '/data';
  }
  if (parts) {
    return basePart + parts.postfix;
  }
  return basePart + defaultEndOfUrl;
}

function getStyleUrl(layerName: string, touchpointType: string, groupId: string, propertyUuid: string, version?: any): string {
  let layerUrlName = getLayerUrlName(layerName);
  const emailPostfix = emailTouchpointsUriMap[touchpointType];
  layerUrlName = layerUrlName == 'photocontest' && emailPostfix ? layerName : layerUrlName;

  let basePart = '/api/' + propertyUuid + '/properties/layers/' + layerUrlName + '/touchpoints/' + groupId + '/';
  if (version !== undefined) {
    basePart += 'versions/' + version + '/';
  }

  if (emailPostfix) {
    return basePart + emailPostfix + '/styling';
  } else {
    basePart +=
      layerName === 'pre-stay' ||
      layerName === 'group-advocacy' ||
      touchpointType === 'photo-river' ||
      touchpointType === 'pre-arrival-email' ||
      touchpointType === 'poststay-email-image'
        ? touchpointType + '/'
        : '';
  }

  return basePart + 'styling';
}

function getIntegrationUrl(layerName: string, touchpointType: string, groupId: string, propertyUuid: string): string {
  const layerUrlName = getLayerUrlName(layerName);
  const url = String.prototype.concat.apply('', [
    '/api/' + propertyUuid + '/properties/layers/',
    layerUrlName,
    '/touchpoints/',
    groupId,
    '/',
    layerName === 'pre-stay' || layerName === 'group-advocacy' || touchpointType === 'photo-river' ? touchpointType + '/' : '',
    'integration',
  ] as any);
  return url;
}

export const endpointUrl = {
  getDataUrl,
  getStyleUrl,
  getIntegrationUrl,
};
